<ng-container *ngIf="vm$ | async as vm; else loadingOrError">
  <div class="edit-page">
    <header class="edit-header">
      <button mat-icon-button (click)="cancel()" matTooltip="Volver" class="edit-back-btn" type="button">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <mat-icon class="edit-header-icon">person</mat-icon>
      <div class="edit-header-text">
        <h1 class="edit-title">{{ vm.isNewMode ? 'Nuevo miembro' : vm.personName }}</h1>
      </div>
      <span *ngIf="personForm.dirty" class="changes-indicator">Cambios sin guardar</span>
    </header>

    <aside *ngIf="!vm.isNewMode && vm.person" class="community-context" aria-label="Comunidad actual">
      <mat-icon>church</mat-icon>
      <div><span>Comunidad actual</span><strong>{{ vm.person.communityName }}</strong></div>
      <p>Los cambios de comunidad se gestionan desde el historial.</p>
    </aside>

    <form [formGroup]="personForm" (ngSubmit)="save()" class="member-form">
      <mat-card class="form-section form-section--primary">
        <mat-card-header>
          <mat-icon mat-card-avatar>badge</mat-icon>
          <mat-card-title>Datos esenciales</mat-card-title>
          <mat-card-subtitle>Los campos con * son obligatorios.</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="form-grid">
          <app-unit-selector
            *ngIf="vm.isNewMode"
            class="full-width"
            formControlName="membershipUnitId"
            label="Comunidad de pertenencia"
            [required]="true"
            [unitSource]="vm.authorizedUnits"
            [showClearButton]="!vm.membershipUnitReadonly"
            [groupByLevel]="true"
            groupByMode="ancestors">
          </app-unit-selector>

          <mat-form-field>
            <mat-label>Nombre</mat-label>
            <input matInput formControlName="firstName" autocomplete="given-name" />
            <mat-error *ngIf="personForm.get('firstName')?.hasError('required')">El nombre es requerido</mat-error>
            <mat-error *ngIf="personForm.get('firstName')?.hasError('minlength')">Mínimo 2 caracteres</mat-error>
            <mat-error *ngIf="personForm.get('firstName')?.hasError('maxlength')">Máximo 100 caracteres</mat-error>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Apellido</mat-label>
            <input matInput formControlName="lastName" autocomplete="family-name" />
            <mat-error *ngIf="personForm.get('lastName')?.hasError('required')">El apellido es requerido</mat-error>
            <mat-error *ngIf="personForm.get('lastName')?.hasError('minlength')">Mínimo 2 caracteres</mat-error>
            <mat-error *ngIf="personForm.get('lastName')?.hasError('maxlength')">Máximo 100 caracteres</mat-error>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Tipo de documento</mat-label>
            <mat-select formControlName="documentType">
              <mat-option *ngFor="let t of documentTypes" [value]="t.value">{{ t.label }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Número de documento</mat-label>
            <input matInput formControlName="documentNumber" inputmode="numeric" autocomplete="off" />
            <mat-error *ngIf="personForm.get('documentNumber')?.hasError('maxlength')">Máximo 50 caracteres</mat-error>
          </mat-form-field>

          <app-date-picker
            label="Fecha de nacimiento"
            formControlName="birthDate"
            [max]="maxBirthDate"
            [errorMessages]="{
              matDatepickerParse: 'Ingresá una fecha válida con formato dd/mm/aaaa.',
              matDatepickerMax: 'La fecha de nacimiento no puede ser posterior a hoy.',
              futureBirthDate: 'La fecha de nacimiento no puede ser posterior a hoy.'
            }">
          </app-date-picker>

          <app-age-status-field [birthDateControl]="birthDateControl" [ageStatusOverrideControl]="ageStatusOverrideControl"></app-age-status-field>
        </mat-card-content>
      </mat-card>

      <mat-card class="form-section">
        <mat-card-header>
          <mat-icon mat-card-avatar>contacts</mat-icon>
          <mat-card-title>Datos de Contacto</mat-card-title>
        </mat-card-header>
        <mat-card-content class="form-grid">
          <mat-form-field>
            <mat-label>Teléfono</mat-label>
            <input matInput formControlName="phone" inputmode="tel" autocomplete="tel" (input)="formatPhoneControlWhileTyping($event)" (blur)="formatPhoneControlForDisplay()" />
            <mat-error *ngIf="personForm.get('phone')?.hasError('maxlength')">Máximo 20 caracteres</mat-error>
            <mat-error *ngIf="personForm.get('phone')?.hasError('invalidPhone')">El teléfono debe ser válido</mat-error>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Email</mat-label>
            <input matInput formControlName="email" type="email" autocomplete="email" />
            <mat-error *ngIf="personForm.get('email')?.hasError('email')">El email debe ser válido</mat-error>
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Dirección</mat-label>
            <input matInput formControlName="address" autocomplete="street-address" />
            <mat-error *ngIf="personForm.get('address')?.hasError('maxlength')">Máximo 250 caracteres</mat-error>
          </mat-form-field>
        </mat-card-content>
      </mat-card>

      <mat-expansion-panel class="optional-section">
        <mat-expansion-panel-header>
          <mat-panel-title><mat-icon>person</mat-icon> Información complementaria</mat-panel-title>
          <mat-panel-description>Datos personales y administrativos</mat-panel-description>
        </mat-expansion-panel-header>
        <div class="form-grid optional-section__content">
          <mat-form-field>
            <mat-label>Género</mat-label>
            <mat-select formControlName="gender">
              <mat-option [value]="null">Sin seleccionar</mat-option>
              <mat-option *ngFor="let g of genders" [value]="g.value">{{ g.label }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Ocupación</mat-label>
            <input matInput formControlName="occupation" autocomplete="organization-title" />
            <mat-error *ngIf="personForm.get('occupation')?.hasError('maxlength')">Máximo 150 caracteres</mat-error>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Lugar de nacimiento</mat-label>
            <input matInput formControlName="placeOfBirth" />
            <mat-error *ngIf="personForm.get('placeOfBirth')?.hasError('maxlength')">Máximo 200 caracteres</mat-error>
          </mat-form-field>

          <mat-form-field>
            <mat-label>País de nacimiento</mat-label>
            <input matInput [formControl]="countryOfBirthSearchControl" [matAutocomplete]="countryOfBirthAutocomplete" placeholder="Buscar" />
            <mat-autocomplete #countryOfBirthAutocomplete="matAutocomplete" [displayWith]="displayCountry.bind(this)" (optionSelected)="onCountryOfBirthSelected($event)">
              <mat-option [value]="null">Sin seleccionar</mat-option>
              <mat-option *ngFor="let c of filterCountries(vm.countries, countryOfBirthSearchControl.value)" [value]="c">{{ c.name }} ({{ c.iso2Code }})</mat-option>
              <mat-option *ngIf="filterCountries(vm.countries, countryOfBirthSearchControl.value).length === 0" disabled>No se encontraron países</mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Nacionalidad</mat-label>
            <input matInput [formControl]="nationalitySearchControl" [matAutocomplete]="nationalityAutocomplete" placeholder="Buscar" />
            <mat-autocomplete #nationalityAutocomplete="matAutocomplete" [displayWith]="displayCountry.bind(this)" (optionSelected)="onNationalitySelected($event)">
              <mat-option [value]="null">Sin seleccionar</mat-option>
              <mat-option *ngFor="let c of filterCountries(vm.countries, nationalitySearchControl.value)" [value]="c">{{ c.name }} ({{ c.iso2Code }})</mat-option>
              <mat-option *ngIf="filterCountries(vm.countries, nationalitySearchControl.value).length === 0" disabled>No se encontraron países</mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Idiomas</mat-label>
            <input matInput formControlName="languages" placeholder="Ej.: Español, Inglés" />
            <mat-error *ngIf="personForm.get('languages')?.hasError('maxlength')">Máximo 500 caracteres</mat-error>
          </mat-form-field>

          <section class="relationship-person-section full-width">
            <h2>Situación de pareja</h2>
            <div class="relationship-person-section__fields">
              <mat-form-field>
                <mat-label>Situación</mat-label>
                <mat-select formControlName="relationshipSituation">
                  <mat-option *ngFor="let situation of relationshipSituations" [value]="situation.value">{{ situation.label }}</mat-option>
                </mat-select>
              </mat-form-field>

              <div class="relationship-person-field" *ngIf="personForm.get('relationshipSituation')?.value !== 'Single' && personForm.get('relationshipSituation')?.value !== 'Unspecified'">
                <mat-form-field>
                  <mat-label>Persona vinculada</mat-label>
                  <input matInput [formControl]="relationshipPersonSearchControl" [matAutocomplete]="relationshipPersonAutocomplete" placeholder="Buscá un miembro o escribí un nombre" />
                  <button *ngIf="personForm.get('relationshipPersonId')?.value || personForm.get('relationshipPersonName')?.value" mat-icon-button matSuffix type="button" aria-label="Quitar persona vinculada" (click)="clearRelationshipPerson()"><mat-icon>close</mat-icon></button>
                  <mat-autocomplete #relationshipPersonAutocomplete="matAutocomplete" [displayWith]="displayRelationshipPerson.bind(this)" (optionSelected)="onRelationshipPersonSelected($event.option.value)">
                    <mat-option *ngFor="let option of filteredRelationshipPeople$ | async" [value]="option">{{ option.name }}<small *ngIf="option.subtitle"> · {{ option.subtitle }}</small></mat-option>
                  </mat-autocomplete>
                  <mat-error *ngIf="personForm.get('relationshipPersonName')?.hasError('maxlength')">Máximo 200 caracteres</mat-error>
                </mat-form-field>
                <app-member-link-source *ngIf="personForm.get('relationshipPersonId')?.value || personForm.get('relationshipPersonName')?.value" [personId]="personForm.get('relationshipPersonId')?.value"></app-member-link-source>
              </div>
            </div>
          </section>

          <mat-form-field>
            <mat-label>Religión anterior</mat-label>
            <input matInput formControlName="previousReligion" />
            <mat-error *ngIf="personForm.get('previousReligion')?.hasError('maxlength')">Máximo 150 caracteres</mat-error>
          </mat-form-field>
        </div>
      </mat-expansion-panel>

      <footer class="edit-footer">
        <p *ngIf="personForm.dirty" class="edit-footer__hint">Tus cambios se guardarán al confirmar.</p>
        <span class="edit-footer__spacer"></span>
        <button mat-button type="button" (click)="cancel()" [disabled]="isSaving$ | async">Cancelar</button>
        <button *ngIf="canManageMemberData" mat-flat-button color="primary" type="submit" [disabled]="isSaving$ | async">
          <mat-spinner *ngIf="isSaving$ | async" diameter="20" class="button-spinner"></mat-spinner>
          <mat-icon *ngIf="!(isSaving$ | async)">save</mat-icon>
          {{ (isSaving$ | async) ? 'Guardando...' : (vm.isNewMode ? 'Guardar miembro' : 'Guardar cambios') }}
        </button>
      </footer>
    </form>
  </div>
</ng-container>

<ng-template #loadingOrError>
  <div class="edit-page">
    <div class="edit-header">
      <button mat-icon-button (click)="cancel()" matTooltip="Volver" class="edit-back-btn" type="button"><mat-icon>arrow_back</mat-icon></button>
      <mat-icon class="edit-header-icon">person</mat-icon>
      <div class="edit-header-text"><h1 class="edit-title">{{ (error$ | async) ? 'Error' : 'Cargando persona' }}</h1></div>
    </div>
    <mat-card class="edit-card">
      <mat-card-content class="loading-state" *ngIf="!(error$ | async); else errorState"><mat-spinner></mat-spinner><p>Cargando datos de la persona...</p></mat-card-content>
    </mat-card>
    <ng-template #errorState>
      <mat-card class="edit-card"><mat-card-content><div class="error-container" *ngIf="error$ | async as error"><mat-icon color="warn">error</mat-icon><p>{{ error }}</p></div></mat-card-content></mat-card>
    </ng-template>
  </div>
</ng-template>
